import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Gift, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth, API } from "../App";
import { toast } from "sonner";

/**
 * Promo code card for existing users to redeem a code post-registration.
 * Variants:
 *   - "banner": compact inline banner (used inside Dashboard trial banner)
 *   - "card":   full card (used on Profile page)
 */
const PromoCodeCard = ({ variant = "card", onApplied }) => {
  const { getAuthHeaders } = useAuth();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [appliedInfo, setAppliedInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      if (res.data?.promo_code) {
        setAppliedInfo({
          code: res.data.promo_code,
          label: res.data.promo_label,
          trial_expires_at: res.data.trial_expires_at,
        });
      }
    } catch {
      // ignore — card simply renders the input form
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleApply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      toast.error("Please enter a promo code");
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/promo/apply`,
        { promo_code: trimmed },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(res.data.message || "Promo code applied!");
      setAppliedInfo({
        code: trimmed,
        label: res.data.message,
        trial_expires_at: res.data.trial_expires_at,
      });
      setCode("");
      if (onApplied) onApplied(res.data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to apply promo code";
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  if (loading) return null;

  // Already redeemed view
  if (appliedInfo) {
    const expiresStr = appliedInfo.trial_expires_at
      ? new Date(appliedInfo.trial_expires_at).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

    if (variant === "banner") {
      return (
        <div
          className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
          data-testid="promo-applied-banner"
        >
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>
            Promo <strong>{appliedInfo.code}</strong> active
            {expiresStr ? ` — access until ${expiresStr}` : ""}
          </span>
        </div>
      );
    }

    return (
      <div
        className="bg-white rounded-xl border border-emerald-200 p-6"
        data-testid="promo-applied-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-1">Promo code active</h3>
            <p className="text-slate-600 text-sm">
              Code <span className="font-mono font-semibold text-emerald-700">{appliedInfo.code}</span> is applied to your account.
              {expiresStr && (
                <>
                  {" "}Extended access until <strong>{expiresStr}</strong>.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Redeem form
  if (variant === "banner") {
    return (
      <div className="mt-3 flex flex-col sm:flex-row gap-2" data-testid="promo-banner-form">
        <div className="relative flex-1">
          <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Have a promo code? Enter it here"
            className="pl-9 h-10 uppercase tracking-wide text-sm bg-white"
            data-testid="promo-banner-input"
            autoComplete="off"
          />
        </div>
        <Button
          onClick={handleApply}
          disabled={applying || !code.trim()}
          className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full h-10"
          data-testid="promo-banner-apply-btn"
        >
          {applying ? "Applying…" : "Apply"}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="promo-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 mb-1">Have a promo code?</h3>
          <p className="text-slate-600 text-sm">
            Beta testers and early supporters: enter your code to unlock extended free access.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter promo code"
          className="uppercase tracking-wide flex-1"
          data-testid="promo-card-input"
          autoComplete="off"
        />
        <Button
          onClick={handleApply}
          disabled={applying || !code.trim()}
          className="bg-primary text-white hover:bg-primary/90 rounded-full"
          data-testid="promo-card-apply-btn"
        >
          {applying ? "Applying…" : "Apply code"}
        </Button>
      </div>
    </div>
  );
};

export default PromoCodeCard;
